/**
 * ParticleSystem Behavior Editor
 * Custom UI for editing particle system behaviors with shape-conditional parameters
 * and debug wireframe overlay for shape visualization.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ParticleSystem } from 'trace-viewer/behaviors';
import type { ParticleSystemData, ParticleShape, ParticleNoise, Range, Keyframe } from 'trace-viewer/behaviors';
import { ParticleShapeType, ParticleTextureMode } from 'trace-viewer/behaviors';
import {
    convertToPremultiplied,
    fitParticleTextureToPo2,
    diagnoseParticleTexture,
    PARTICLE_TEXTURE_MAX_PX,
} from 'trace-viewer/editor';
import type { ParticleSource } from 'trace-viewer/editor';
import { NATIVE_IMAGE_EXTENSIONS, toAcceptString } from 'trace-viewer/scene';

// Babylon.js imports for debug overlay
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Tools } from "@babylonjs/core/Misc/tools";
import type { ResourceManager } from 'trace-viewer/scene';

// ============================================================================
// Shared Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    } as React.CSSProperties,
    sectionHeader: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '8px',
        paddingBottom: '4px',
        borderBottom: '1px solid #3a3a3a',
    } as React.CSSProperties,
};

// ============================================================================
// Debug Overlay Hook
// ============================================================================

/**
 * Creates a wireframe debug mesh in the 3D viewport showing the particle emission shape.
 * Shown when shape fields have focus, hidden when they blur.
 */
function useShapeDebugOverlay(
    behavior: ParticleSystem,
    shape: ParticleShape,
    visible: boolean
) {
    const meshRef = useRef<Mesh | null>(null);
    const materialRef = useRef<StandardMaterial | null>(null);
    // Debounce blur so the mesh doesn't flicker when tabbing between shape fields
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [showOverlay, setShowOverlay] = useState(false);

    useEffect(() => {
        if (visible) {
            if (blurTimerRef.current) {
                clearTimeout(blurTimerRef.current);
                blurTimerRef.current = null;
            }
            setShowOverlay(true);
        } else {
            blurTimerRef.current = setTimeout(() => {
                setShowOverlay(false);
            }, 150);
        }
        return () => {
            if (blurTimerRef.current) {
                clearTimeout(blurTimerRef.current);
            }
        };
    }, [visible]);

    const boxSizeX = shape.boxSize?.[0];
    const boxSizeY = shape.boxSize?.[1];
    const boxSizeZ = shape.boxSize?.[2];

    useEffect(() => {
        // Dispose old mesh
        if (meshRef.current) {
            meshRef.current.dispose();
            meshRef.current = null;
        }

        if (!showOverlay) {
            if (materialRef.current) {
                materialRef.current.dispose();
                materialRef.current = null;
            }
            return;
        }

        const scene = behavior.sceneObject.babylonScene;
        const parentNode = behavior.sceneObject.transform;
        if (!scene || !parentNode) return;

        // Create wireframe material
        if (!materialRef.current) {
            const mat = new StandardMaterial("_debugShapeMat", scene);
            mat.wireframe = true;
            mat.emissiveColor = new Color3(0.3, 1.0, 0.3);
            mat.disableLighting = true;
            materialRef.current = mat;
        }

        let mesh: Mesh | null = null;

        switch (shape.type) {
            case ParticleShapeType.Cone: {
                const radius = shape.radius ?? 0.5;
                const angleDeg = shape.angle ?? 25;
                const height = Math.max(radius * 2, 0.5);
                const topRadius = radius + Math.tan(Tools.ToRadians(angleDeg / 2)) * height;
                mesh = MeshBuilder.CreateCylinder("_debugCone", {
                    diameterTop: topRadius * 2,
                    diameterBottom: radius * 2,
                    height,
                    tessellation: 24,
                }, scene);
                mesh.position.y = height / 2;
                break;
            }
            case ParticleShapeType.Sphere: {
                const radius = shape.radius ?? 1;
                mesh = MeshBuilder.CreateSphere("_debugSphere", {
                    diameter: radius * 2,
                    segments: 16,
                }, scene);
                break;
            }
            case ParticleShapeType.Box: {
                const boxSize = shape.boxSize ?? [1, 1, 1];
                mesh = MeshBuilder.CreateBox("_debugBox", {
                    width: boxSize[0],
                    height: boxSize[1],
                    depth: boxSize[2],
                }, scene);
                break;
            }
        }

        if (mesh) {
            mesh.material = materialRef.current;
            mesh.parent = parentNode;
            mesh.isPickable = false;
            mesh.renderingGroupId = 1;
            meshRef.current = mesh;
        }

        return () => {
            if (meshRef.current) {
                meshRef.current.dispose();
                meshRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boxSize covered by individual boxSizeX/Y/Z deps
    }, [showOverlay, shape.type, shape.radius, shape.angle,
        boxSizeX, boxSizeY, boxSizeZ,
        behavior]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            meshRef.current?.dispose();
            materialRef.current?.dispose();
        };
    }, []);
}

// ============================================================================
// Field Components
// ============================================================================

const NumberInput: React.FC<{
    label: string;
    value: number | undefined;
    onChange: (value: number) => void;
    step?: number;
    min?: number;
    max?: number;
    description?: string;
    suffix?: string;
    onFocus?: () => void;
    onBlur?: () => void;
}> = ({ label, value, onChange, step = 0.1, min, max, description, suffix, onFocus, onBlur }) => {
    const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '');

    useEffect(() => {
        setLocalValue(value?.toString() ?? '');
    }, [value]);

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <input
                type="number"
                value={localValue}
                onChange={(e) => {
                    const text = e.target.value;
                    setLocalValue(text);
                    if (text !== '' && text !== '-') {
                        const num = parseFloat(text);
                        if (!isNaN(num)) onChange(num);
                    }
                }}
                onBlur={() => {
                    if (localValue === '' || localValue === '-') {
                        onChange(0);
                        setLocalValue('0');
                    }
                    onBlur?.();
                }}
                onFocus={onFocus}
                step={step}
                min={min}
                max={max}
                style={{ ...styles.input, width: '80px' }}
            />
            {suffix && <span style={{ fontSize: '11px', color: '#666' }}>{suffix}</span>}
        </div>
    );
};

const RangeField: React.FC<{
    label: string;
    value: Range | undefined;
    onChange: (value: Range) => void;
    step?: number;
    description?: string;
}> = ({ label, value, onChange, step = 0.1, description }) => {
    const range = value ?? { min: 0, max: 0 };
    const [localMin, setLocalMin] = useState(range.min.toString());
    const [localMax, setLocalMax] = useState(range.max.toString());

    useEffect(() => {
        setLocalMin((value?.min ?? 0).toString());
        setLocalMax((value?.max ?? 0).toString());
    }, [value?.min, value?.max]);

    const commitMin = (text: string) => {
        const num = parseFloat(text);
        if (!isNaN(num)) onChange({ ...range, min: num });
    };
    const commitMax = (text: string) => {
        const num = parseFloat(text);
        if (!isNaN(num)) onChange({ ...range, max: num });
    };

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <span style={{ fontSize: '11px', color: '#888' }}>Min</span>
            <input
                type="number"
                value={localMin}
                onChange={(e) => {
                    setLocalMin(e.target.value);
                    commitMin(e.target.value);
                }}
                onBlur={() => {
                    if (localMin === '' || localMin === '-') {
                        onChange({ ...range, min: 0 });
                        setLocalMin('0');
                    }
                }}
                step={step}
                style={{ ...styles.input, width: '60px' }}
            />
            <span style={{ fontSize: '11px', color: '#888' }}>Max</span>
            <input
                type="number"
                value={localMax}
                onChange={(e) => {
                    setLocalMax(e.target.value);
                    commitMax(e.target.value);
                }}
                onBlur={() => {
                    if (localMax === '' || localMax === '-') {
                        onChange({ ...range, max: 0 });
                        setLocalMax('0');
                    }
                }}
                step={step}
                style={{ ...styles.input, width: '60px' }}
            />
        </div>
    );
};

/**
 * Parse alpha from hex color string. Returns 0-1.
 * Supports #RRGGBB (returns 1) and #RRGGBBAA.
 */
export function parseAlpha(hex: string): number {
    if (!hex || hex.length < 7) return 1;
    if (hex.length >= 9) {
        const a = parseInt(hex.substring(7, 9), 16);
        return isNaN(a) ? 1 : a / 255;
    }
    return 1;
}

/** Set alpha on a hex color string. alpha is 0-1. Returns #RRGGBBAA. */
export function setAlpha(hex: string, alpha: number): string {
    const rgb = (hex?.startsWith('#') ? hex.substring(0, 7) : '#ffffff');
    const aa = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0').toUpperCase();
    return rgb + aa;
}

/**
 * Apply a new hex color while preserving the existing alpha suffix.
 * If newHex is 7-char (#RRGGBB) and existingHex has alpha (#RRGGBBAA), keeps the AA.
 */
export function preserveAlpha(newHex: string, existingHex: string | undefined): string {
    if (newHex.length === 7 && existingHex && existingHex.length >= 9) {
        return newHex + existingHex.substring(7, 9);
    }
    return newHex;
}

const ColorInput: React.FC<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    description?: string;
    showAlpha?: boolean;
}> = ({ label, value, onChange, description, showAlpha = false }) => {
    const hexValue = value?.startsWith('#') ? value.substring(0, 7) : '#ffffff';
    const alpha = parseAlpha(value);

    return (
        <div style={{ ...styles.fieldRow, flexWrap: showAlpha ? 'wrap' : undefined }} title={description}>
            <span style={styles.label}>{label}</span>
            <input
                type="color"
                value={hexValue}
                onChange={(e) => {
                    onChange(showAlpha ? preserveAlpha(e.target.value, value) : e.target.value);
                }}
                style={{ width: '40px', height: '24px', padding: 0, border: 'none', cursor: 'pointer' }}
            />
            <input
                type="text"
                value={value || ''}
                onChange={(e) => {
                    onChange(showAlpha ? preserveAlpha(e.target.value, value) : e.target.value);
                }}
                style={{ ...styles.input, width: '100px' }}
                placeholder={showAlpha ? "#RRGGBBAA" : "#RRGGBB"}
            />
            {showAlpha && (
                <>
                    <span style={{ fontSize: '11px', color: '#888', marginLeft: '4px' }}>Alpha</span>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={alpha}
                        onChange={(e) => onChange(setAlpha(value || '#ffffff', parseFloat(e.target.value)))}
                        style={{ width: '60px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '11px', color: '#aaa', minWidth: '28px' }}>
                        {Math.round(alpha * 100)}%
                    </span>
                </>
            )}
        </div>
    );
};

// ============================================================================
// Texture Import (drag-and-drop + import-mode re-bake)
//
// Particle textures are stored as baked premultiplied RGBA. The author's original
// source + chosen import mode are NOT persisted — they live in this component's state,
// volatile by design. While the source is held (the texture was imported this editing
// session), changing the import mode re-bakes the stored texture live. Once the editor
// reopens the scene or the selection moves to a different object, the source is gone and
// the texture is shown read-only — re-import to change its format.
// See trace-viewer docs-internal/particle-texture-format.md (option F).
// ============================================================================

const ASSET_ACCEPT = toAcceptString(NATIVE_IMAGE_EXTENSIONS);

const isValidImageFile = (file: File): boolean => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    // Native (browser-decodable) image formats only — particle import bakes via
    // createImageBitmap, which can't decode the GeoTIFF formats in IMAGE_EXTENSIONS.
    return NATIVE_IMAGE_EXTENSIONS.has(ext);
};

const stripAssetsPrefix = (path: string): string =>
    path.replace(/^\.\/assets\//, '').replace(/^assets\//, '');

const IMPORT_MODE_LABELS: Record<ParticleTextureMode, string> = {
    [ParticleTextureMode.Additive]: 'Additive (glow / opaque)',
    [ParticleTextureMode.Alpha]: 'Alpha (transparent)',
    [ParticleTextureMode.Premultiplied]: 'Premultiplied (no conversion)',
};

const ParticleTextureImport: React.FC<{
    /** Baked premultiplied texture path stored in scene data (`data.texture`). */
    value: string;
    onChange: (value: string) => void;
    resources?: ResourceManager;
}> = ({ value, onChange, resources }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isFixing, setIsFixing] = useState(false);
    const [fixError, setFixError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // Dimensions of the baked texture currently in the ResourceManager, for the info/warnings.
    const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
    // The original source + mode behind the CURRENT `value`, held only while this editor
    // is mounted on the object that imported it. null = no editable source (reopened scene
    // or selection moved). Reset whenever `value` changes to a path we didn't just bake.
    const [source, setSource] = useState<ParticleSource | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounterRef = useRef(0);
    // Path we most recently baked, so the `[value]` reset effect can tell "value changed
    // because I baked it" (keep source) from "value changed externally" (drop source).
    const bakedPathRef = useRef<string | null>(null);

    const hasEditableSource = source !== null && value === bakedPathRef.current;

    // Drop the held source when `value` changes to anything we didn't just bake — e.g. the
    // editor remounted this section onto a different object, or the scene was reopened. Any
    // texture change (import, clear, successful fix, mode re-bake, selection move) also clears a
    // stale fix error — a failed fix leaves `value` unchanged, so its error correctly persists.
    useEffect(() => {
        if (value !== bakedPathRef.current) {
            setSource(null);
        }
        setFixError(null);
    }, [value]);

    // Preview + dimensions reflect the baked texture currently in the ResourceManager.
    useEffect(() => {
        if (value && resources) {
            const blob = resources.getAsset(value);
            if (blob) {
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                let cancelled = false;
                const img = new Image();
                img.onload = () => {
                    if (!cancelled) setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                };
                img.onerror = () => { if (!cancelled) setDimensions(null); };
                img.src = url;
                return () => { cancelled = true; URL.revokeObjectURL(url); };
            }
        }
        setPreviewUrl(null);
        setDimensions(null);
    }, [value, resources]);

    useEffect(() => {
        const reset = () => {
            dragCounterRef.current = 0;
            setIsDragging(false);
        };
        document.addEventListener('drop', reset);
        document.addEventListener('dragend', reset);
        return () => {
            document.removeEventListener('drop', reset);
            document.removeEventListener('dragend', reset);
        };
    }, []);

    /** Bake `file` with `mode`, store it, point `data.texture` at it, and hold the source. */
    const bakeAndStore = useCallback(async (file: File, mode: ParticleTextureMode) => {
        if (!resources) return;
        const converted = await convertToPremultiplied(file, mode);
        const assetKey = `assets/${converted.name}`;
        const storedPath = await resources.addAssetWithDedup(assetKey, converted);
        const texturePath = `./${storedPath}`;
        // Record the baked path BEFORE onChange so the `[value]` reset effect keeps `source`.
        bakedPathRef.current = texturePath;
        setSource({ file, mode });
        onChange(texturePath);
    }, [resources, onChange]);

    const handleImport = useCallback(async (file: File) => {
        if (!isValidImageFile(file) || !resources) return;
        // Default new imports to Additive (most particle sprites are opaque glow textures).
        await bakeAndStore(file, ParticleTextureMode.Additive);
    }, [resources, bakeAndStore]);

    const handleModeChange = useCallback(async (mode: ParticleTextureMode) => {
        if (!source || mode === source.mode) return;
        await bakeAndStore(source.file, mode);
    }, [source, bakeAndStore]);

    const handleClear = useCallback(() => {
        bakedPathRef.current = null;
        setSource(null);
        onChange('');
    }, [onChange]);

    // Resize the texture to a square po2 ≤256 (content centred, transparent-padded — Babylon
    // particle quads are square). When the original source is still held, fix it and re-bake
    // (preserves import mode); otherwise resize the already-baked premultiplied blob in place
    // (scale + transparent pad preserve premultiplication).
    const handleFix = useCallback(async () => {
        if (!resources) return;
        setIsFixing(true);
        setFixError(null);
        try {
            if (source && value === bakedPathRef.current) {
                const fixed = await fitParticleTextureToPo2(source.file);
                await bakeAndStore(fixed, source.mode);
                return;
            }
            const blob = resources.getAsset(value);
            if (!blob) {
                setFixError('Texture asset is no longer in the scene — try re-importing.');
                return;
            }
            const sourceName = stripAssetsPrefix(value);
            const fixed = await fitParticleTextureToPo2(new File([blob], sourceName, { type: blob.type || 'image/png' }));
            const storedPath = await resources.addAssetWithDedup(`assets/${fixed.name}`, fixed);
            const texturePath = `./${storedPath}`;
            bakedPathRef.current = null;
            setSource(null);
            onChange(texturePath);
        } catch (err) {
            console.error('Failed to fix particle texture size', err);
            setFixError(err instanceof Error ? err.message : 'Failed to resize texture');
        } finally {
            setIsFixing(false);
        }
    }, [resources, source, value, bakeAndStore, onChange]);

    const displayName = value ? stripAssetsPrefix(value) : '';

    // Sizing warnings + the square po2 fix target come from the shared viewer editor, so this
    // editor and TraceFrontend stay in lockstep on conditions, copy, and target.
    const diagnosis = dimensions && diagnoseParticleTexture(dimensions.width, dimensions.height);

    return (
        <div>
            {/* Drop zone / current texture */}
            <div style={styles.fieldRow} title="Particle texture (stored premultiplied)">
                <span style={styles.label}>Texture</span>
                <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current++; if (e.dataTransfer?.types.includes('Files')) setIsDragging(true); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current--; if (dragCounterRef.current === 0) setIsDragging(false); }}
                    onDrop={async (e) => {
                        e.preventDefault(); e.stopPropagation();
                        dragCounterRef.current = 0; setIsDragging(false);
                        const file = Array.from(e.dataTransfer?.files || []).find(isValidImageFile);
                        if (file) await handleImport(file);
                    }}
                    style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '6px 8px',
                        backgroundColor: isDragging ? '#3a4a3a' : '#333',
                        border: `1px ${isDragging ? 'dashed' : 'solid'} ${isDragging ? '#4CAF50' : '#555'}`,
                        borderRadius: '3px', cursor: 'pointer', minHeight: '28px',
                        transition: 'all 0.15s',
                    }}
                >
                    {previewUrl && (
                        <div style={{
                            width: '24px', height: '24px', borderRadius: '2px',
                            backgroundImage: `url(${previewUrl})`, backgroundSize: 'cover',
                            backgroundPosition: 'center', flexShrink: 0,
                        }} />
                    )}
                    <span style={{
                        flex: 1, fontSize: '12px',
                        color: value ? '#fff' : '#888',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {value ? displayName : 'Drop image or click to browse'}
                    </span>
                    {value && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handleClear(); }}
                            style={{
                                padding: '2px 4px', backgroundColor: 'transparent',
                                border: 'none', color: '#888', cursor: 'pointer',
                                fontSize: '12px', lineHeight: 1,
                            }}
                            title="Clear"
                        >
                            &times;
                        </button>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ASSET_ACCEPT}
                    onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await handleImport(file);
                        e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Dimensions (always shown for a loaded texture) + warnings from the shared
                viewer editor. The px readout is deliberately NOT gated on `diagnosis` so a
                healthy texture keeps showing its size. */}
            {value && dimensions && (
                <div style={{ margin: '-2px 0 8px 108px', fontSize: '11px', lineHeight: 1.5 }}>
                    <span style={{ color: '#999' }}>
                        {dimensions.width} × {dimensions.height} px
                    </span>
                    {diagnosis?.warnings.map((w) => (
                        <div key={w.code} style={{ color: '#e0a030' }}>
                            ⚠ {w.message}
                        </div>
                    ))}
                    {diagnosis?.needsFix && (
                        <button
                            onClick={() => { void handleFix(); }}
                            disabled={isFixing}
                            title={`Resize to ${diagnosis.fitTarget.width} × ${diagnosis.fitTarget.height} px (square power-of-two, max ${PARTICLE_TEXTURE_MAX_PX}; non-square content is centred with transparent padding)`}
                            style={{
                                marginTop: '3px', padding: '2px 8px',
                                backgroundColor: '#3a3a3a', border: '1px solid #555',
                                borderRadius: '3px', color: '#e0a030',
                                fontSize: '11px', cursor: isFixing ? 'default' : 'pointer',
                                opacity: isFixing ? 0.6 : 1,
                            }}
                        >
                            {isFixing
                                ? 'Fixing…'
                                : `Fix → ${diagnosis.fitTarget.width} × ${diagnosis.fitTarget.height} px`}
                        </button>
                    )}
                    {fixError && (
                        <div style={{ color: '#e06060' }}>
                            ⚠ {fixError}
                        </div>
                    )}
                </div>
            )}

            {/* Import-mode control: live re-bake while the source is held; honest notice when not. */}
            {value && hasEditableSource && source && (
                <>
                    <div style={styles.fieldRow} title="How the source image is converted to premultiplied RGBA">
                        <span style={styles.label}>Import Mode</span>
                        <select
                            value={source.mode}
                            onChange={(e) => { void handleModeChange(e.target.value as ParticleTextureMode); }}
                            style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                        >
                            {Object.values(ParticleTextureMode).map(m => (
                                <option key={m} value={m}>{IMPORT_MODE_LABELS[m]}</option>
                            ))}
                        </select>
                    </div>
                    {source.mode === ParticleTextureMode.Premultiplied && (
                        <div style={{
                            fontSize: '11px', color: '#999', lineHeight: 1.4,
                            margin: '-2px 0 8px 108px',
                        }}>
                            Stored as-is — assumes the source is already premultiplied RGBA.
                        </div>
                    )}
                </>
            )}
            {value && !hasEditableSource && (
                <div style={{
                    fontSize: '11px', color: '#999', lineHeight: 1.4,
                    margin: '-2px 0 8px 108px',
                }}>
                    Baked texture — no editable source in this session.
                    Re-import the original image to change its format.
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Shape Section
// ============================================================================

const ShapeSection: React.FC<{
    shape: ParticleShape;
    onChange: (shape: ParticleShape) => void;
    onFieldFocus: (focused: boolean) => void;
}> = ({ shape, onChange, onFieldFocus }) => {

    const handleTypeChange = (newType: string) => {
        const shapeType = newType as ParticleShapeType;
        const newShape: ParticleShape = { type: shapeType };
        switch (shapeType) {
            case ParticleShapeType.Cone:
                newShape.radius = shape.radius ?? 0.5;
                newShape.angle = shape.angle ?? 25;
                break;
            case ParticleShapeType.Sphere:
                newShape.radius = shape.radius ?? 1;
                break;
            case ParticleShapeType.Box:
                newShape.boxSize = shape.boxSize ?? [1, 1, 1];
                break;
        }
        onChange(newShape);
    };

    return (
        <div>
            {/* Shape Type */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Type</span>
                <select
                    value={shape.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                >
                    {Object.values(ParticleShapeType).map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
            </div>

            {/* Cone: radius + angle */}
            {shape.type === ParticleShapeType.Cone && (
                <>
                    <NumberInput
                        label="Radius"
                        value={shape.radius ?? 0.5}
                        onChange={(v) => onChange({ ...shape, radius: v })}
                        step={0.1}
                        description="Cone base radius (world units)"
                        onFocus={() => onFieldFocus(true)}
                        onBlur={() => onFieldFocus(false)}
                    />
                    <NumberInput
                        label="Angle"
                        value={shape.angle ?? 25}
                        onChange={(v) => onChange({ ...shape, angle: v })}
                        step={1}
                        min={0}
                        max={90}
                        suffix="deg"
                        description="Cone spread angle (0-90 degrees)"
                        onFocus={() => onFieldFocus(true)}
                        onBlur={() => onFieldFocus(false)}
                    />
                </>
            )}

            {/* Sphere: radius */}
            {shape.type === ParticleShapeType.Sphere && (
                <NumberInput
                    label="Radius"
                    value={shape.radius ?? 1}
                    onChange={(v) => onChange({ ...shape, radius: v })}
                    step={0.1}
                    description="Sphere radius (world units)"
                    onFocus={() => onFieldFocus(true)}
                    onBlur={() => onFieldFocus(false)}
                />
            )}

            {/* Box: boxSize x, y, z */}
            {shape.type === ParticleShapeType.Box && (
                <div style={styles.fieldRow}>
                    <span style={styles.label}>Box Size</span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {(['X', 'Y', 'Z'] as const).map((axis, i) => (
                            <React.Fragment key={axis}>
                                <span style={{ color: ['#f88', '#8f8', '#88f'][i], fontSize: '11px' }}>{axis}</span>
                                <input
                                    type="number"
                                    value={(shape.boxSize ?? [1, 1, 1])[i]}
                                    onChange={(e) => {
                                        const newSize = [...(shape.boxSize ?? [1, 1, 1])] as [number, number, number];
                                        newSize[i] = parseFloat(e.target.value) || 0;
                                        onChange({ ...shape, boxSize: newSize });
                                    }}
                                    step={0.1}
                                    style={{ ...styles.input, width: '55px' }}
                                    onFocus={() => onFieldFocus(true)}
                                    onBlur={() => onFieldFocus(false)}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Keyframe Array (Size Over Lifetime)
// ============================================================================

const KeyframeArray: React.FC<{
    label: string;
    value: Keyframe[] | undefined;
    onChange: (value: Keyframe[] | undefined) => void;
}> = ({ label, value, onChange }) => {
    const [expanded, setExpanded] = useState(true);
    const items = value ?? [];

    const handleAdd = () => {
        const lastTime = items.length > 0 ? items[items.length - 1].time : 0;
        onChange([...items, { time: Math.min(lastTime + 0.25, 1), value: 0.1 }]);
    };

    const handleRemove = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems.length > 0 ? newItems : undefined);
    };

    const handleChange = (index: number, field: 'time' | 'value', val: number) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: val };
        onChange(newItems);
    };

    return (
        <div style={{
            marginBottom: '8px',
            backgroundColor: '#363636',
            borderRadius: '4px',
            overflow: 'hidden',
        }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'center',
                    padding: '6px 10px', backgroundColor: '#404040',
                    cursor: 'pointer', userSelect: 'none',
                }}
            >
                <span style={{
                    marginRight: '6px', fontSize: '9px', color: '#888',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                }}>
                    &#x25B6;
                </span>
                <span style={{ fontSize: '12px', color: '#ccc', flex: 1 }}>{label}</span>
                <span style={{ fontSize: '11px', color: '#666' }}>{items.length}</span>
            </div>

            {expanded && (
                <div style={{ padding: '8px' }}>
                    {items.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 8px', backgroundColor: '#2a2a2a',
                                borderRadius: '3px', marginBottom: '4px',
                            }}
                        >
                            <span style={{ fontSize: '11px', color: '#888', minWidth: '28px' }} title="Normalized particle lifetime (0 = spawn, 1 = death)">Life</span>
                            <input
                                type="number"
                                value={item.time}
                                onChange={(e) => handleChange(index, 'time', parseFloat(e.target.value) || 0)}
                                step={0.01} min={0} max={1}
                                style={{ ...styles.input, width: '55px' }}
                                title="0 = spawn, 1 = death"
                            />
                            <span style={{ fontSize: '11px', color: '#888', minWidth: '32px' }} title="Size multiplier at this point in lifetime">Size</span>
                            <input
                                type="number"
                                value={item.value}
                                onChange={(e) => handleChange(index, 'value', parseFloat(e.target.value) || 0)}
                                step={0.01}
                                style={{ ...styles.input, width: '55px' }}
                            />
                            <button
                                onClick={() => handleRemove(index)}
                                style={{
                                    marginLeft: 'auto', padding: '2px 6px',
                                    backgroundColor: 'transparent', border: 'none',
                                    color: '#666', cursor: 'pointer', fontSize: '12px',
                                }}
                                title="Remove keyframe"
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={handleAdd}
                        style={{
                            width: '100%', padding: '6px',
                            backgroundColor: '#3a3a3a', border: '1px dashed #555',
                            borderRadius: '3px', color: '#888', cursor: 'pointer',
                            fontSize: '11px',
                            marginTop: items.length > 0 ? '4px' : 0,
                        }}
                    >
                        + Add Keyframe
                    </button>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Noise Section
// ============================================================================

const NoiseSection: React.FC<{
    noise: ParticleNoise | undefined;
    onChange: (noise: ParticleNoise | undefined) => void;
}> = ({ noise, onChange }) => {
    const enabled = !!noise;
    const strength = noise?.strength ?? [0, 0, 0];

    const handleToggle = () => {
        if (enabled) {
            onChange(undefined);
        } else {
            onChange({ strength: [1, 1, 1], frequency: 0.5, octaves: 2 });
        }
    };

    const updateStrength = (axis: number, val: number) => {
        const newStrength = [...strength] as [number, number, number];
        newStrength[axis] = val;
        onChange({ ...noise!, strength: newStrength });
    };

    return (
        <div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Enabled</span>
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={handleToggle}
                    style={{ cursor: 'pointer' }}
                />
            </div>

            {enabled && (
                <>
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Strength</span>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {(['X', 'Y', 'Z'] as const).map((axis, i) => (
                                <React.Fragment key={axis}>
                                    <span style={{ color: ['#f88', '#8f8', '#88f'][i], fontSize: '11px' }}>{axis}</span>
                                    <input
                                        type="number"
                                        value={strength[i]}
                                        onChange={(e) => updateStrength(i, parseFloat(e.target.value) || 0)}
                                        step={0.1}
                                        style={{ ...styles.input, width: '55px' }}
                                    />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <NumberInput
                        label="Frequency"
                        value={noise.frequency ?? 0.5}
                        onChange={(v) => onChange({ ...noise, frequency: v })}
                        step={0.1}
                        min={0.01}
                        description="Noise frequency — higher = more chaotic"
                    />
                    <NumberInput
                        label="Octaves"
                        value={noise.octaves ?? 2}
                        onChange={(v) => onChange({ ...noise, octaves: Math.max(1, Math.min(4, Math.round(v))) })}
                        step={1}
                        min={1}
                        max={4}
                        description="Noise detail layers (1-4)"
                    />
                    <NumberInput
                        label="Drag"
                        value={noise.drag ?? 0}
                        onChange={(v) => onChange({ ...noise, drag: Math.max(0, Math.min(1, v)) })}
                        step={0.05}
                        min={0}
                        max={1}
                        description="Velocity damping (0 = none, 1 = full)"
                    />
                </>
            )}
        </div>
    );
};

// ============================================================================
// Main Editor Component
// ============================================================================

export interface ParticleSystemEditorProps {
    data: ParticleSystemData;
    behavior: ParticleSystem;
    onChange: (data: ParticleSystemData) => void;
}

export const ParticleSystemEditor: React.FC<ParticleSystemEditorProps> = ({
    data, behavior, onChange,
}) => {
    const [shapeFieldFocused, setShapeFieldFocused] = useState(false);

    const update = useCallback(<K extends keyof ParticleSystemData>(key: K, value: ParticleSystemData[K]) => {
        onChange({ ...data, [key]: value });
    }, [data, onChange]);

    // Debug overlay
    useShapeDebugOverlay(behavior, data.shape, shapeFieldFocused);

    return (
        <div>
            {/* Emission */}
            <div style={styles.sectionHeader}>Emission</div>
            <NumberInput
                label="Emission Rate"
                value={data.emissionRate}
                onChange={(v) => update('emissionRate', v)}
                step={1}
                description="Particles emitted per second"
            />
            <NumberInput
                label="Max Particles"
                value={data.maxParticles}
                onChange={(v) => update('maxParticles', Math.max(1, Math.round(v)))}
                step={10}
                description="Maximum particles alive at once"
            />

            {/* Lifetime */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Lifetime</div>
            <RangeField
                label="Lifetime"
                value={data.lifetime}
                onChange={(v) => update('lifetime', v)}
                step={0.1}
                description="Particle lifetime range in seconds"
            />

            {/* Color */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Color</div>
            <ColorInput
                label="Start Color"
                value={data.startColor}
                onChange={(v) => update('startColor', v)}
                description="Starting particle color (with alpha)"
                showAlpha
            />
            <ColorInput
                label="Start Color 2"
                value={data.startColor2 ?? ''}
                onChange={(v) => update('startColor2', v || undefined)}
                description="Optional second color for random range"
                showAlpha
            />
            <ColorInput
                label="End Color"
                value={data.endColor ?? '#00000000'}
                onChange={(v) => update('endColor', v || undefined)}
                description="Color particles fade to at end of life"
                showAlpha
            />

            {/* Speed & Gravity */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Physics</div>
            <RangeField
                label="Start Speed"
                value={data.startSpeed}
                onChange={(v) => update('startSpeed', v)}
                step={0.1}
                description="Starting speed range (world units/sec)"
            />
            <NumberInput
                label="Gravity"
                value={data.gravityModifier}
                onChange={(v) => update('gravityModifier', v)}
                step={0.1}
                description="Gravity multiplier (-1 = down, 0 = none, 1 = up)"
            />

            {/* Shape */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Shape</div>
            <ShapeSection
                shape={data.shape}
                onChange={(shape) => update('shape', shape)}
                onFieldFocus={setShapeFieldFocused}
            />

            {/* Texture & Rendering */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Rendering</div>
            <ParticleTextureImport
                value={data.texture}
                onChange={(v) => update('texture', v)}
                resources={behavior.sceneObject.resources}
            />

            {/* Size */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Size</div>
            <NumberInput
                label="Particle Scale"
                value={data.particleScale ?? 1}
                onChange={(v) => update('particleScale', v)}
                step={0.1}
                min={0.01}
                description="Overall particle size multiplier (distinct from object transform scale)"
            />
            <KeyframeArray
                label="Size Over Lifetime"
                value={data.sizeOverLifetime}
                onChange={(v) => update('sizeOverLifetime', v)}
            />

            {/* Rotation */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Rotation</div>
            <RangeField
                label="Start Rotation"
                value={data.startRotation}
                onChange={(v) => update('startRotation', v)}
                step={1}
                description="Starting rotation range (degrees)"
            />
            <RangeField
                label="Angular Velocity"
                value={data.angularVelocity}
                onChange={(v) => update('angularVelocity', v)}
                step={1}
                description="Angular velocity range (degrees/sec)"
            />

            {/* Noise / Turbulence */}
            <div style={{ ...styles.sectionHeader, marginTop: '12px' }}>Noise</div>
            <NoiseSection
                noise={data.noise}
                onChange={(noise) => update('noise', noise)}
            />
        </div>
    );
};
